/**
 * 隐私政策 / 服务条款公开页（OPT-054）
 *
 * - 公开路由 /privacy、/terms（无需登录）
 * - 文案来自 content/legal.ts 单一数据源：zh-TW 界面用繁中版，其余语言回退英文
 * - 排版跟随落地页风格（浅色底 + Bebas Neue 品牌字）
 */
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getLegalDoc, type LegalKind } from '@/content/legal'

function LegalPage({ kind }: { kind: LegalKind }) {
  const { t, i18n } = useTranslation()
  const doc = getLegalDoc(kind, i18n.language)
  const brand = t('landing.brand')

  useEffect(() => {
    document.title = `${doc.title} — ${brand}`
  }, [doc.title, brand])

  return (
    <div className="min-h-screen bg-slate-50">
      {/* 顶栏：品牌字回首页（与落地页一致的 Bebas Neue） */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <Link
            to="/"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}
            className="text-xl text-slate-900 hover:opacity-70 transition-opacity"
          >
            {brand}
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1
          style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.03em' }}
          className="text-4xl text-slate-900 mb-2"
        >
          {doc.title}
        </h1>
        <p className="text-xs uppercase tracking-widest text-slate-400 mb-8">
          Last updated · {doc.lastUpdated}
        </p>
        <p className="text-slate-600 leading-relaxed mb-10">{doc.intro}</p>

        {doc.sections.map((section) => (
          <section key={section.heading} className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">{section.heading}</h2>
            {section.paragraphs.map((p, i) => (
              <p key={i} className="text-slate-600 text-sm leading-relaxed mb-2">
                {p}
              </p>
            ))}
          </section>
        ))}

        {/* 页脚：两个法律页互链 */}
        <div className="mt-14 pt-6 border-t border-slate-200 flex gap-5 text-xs text-slate-400">
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">
            {t('landing.footer.privacy')}
          </Link>
          <Link to="/terms" className="hover:text-slate-600 transition-colors">
            {t('landing.footer.terms')}
          </Link>
        </div>
      </main>
    </div>
  )
}

export function PrivacyPage() {
  return <LegalPage kind="privacy" />
}

export function TermsPage() {
  return <LegalPage kind="terms" />
}
